/**
 * SC-04 — Add the reply as speaker notes (AIVA-6755).
 *
 * Appends the reply to the current slide's speaker notes, after one blank line.
 * Existing notes are always kept: pressing the button twice gives two appended
 * blocks, never an overwrite.
 *
 * The PowerPoint JS API has no speaker-notes property (the prototype's
 * `slide.notesPage` never existed), so the notes are written the way DI-02 reads
 * them and Deckorate's Speaker Notes panel writes them: export the slide
 * (`exportAsBase64`, PowerPointApi 1.8), append to its notesSlide part
 * ({@link appendToSlideNotes}), insert the rewritten copy after the original
 * (`insertSlidesFromBase64`) and delete the original. The zip and slide-swap
 * helpers are Deckorate's, imported as-is — that path is already proven in
 * production.
 *
 * Because the slide is replaced by its copy, the copy is selected afterwards so
 * the user stays on the slide they were on.
 *
 * Gap G-2: a host that cannot export or insert slides gets the approved message
 * ({@link NOTES_UNAVAILABLE_ERROR} → "AiVA can't reach the speaker notes in this
 * version of PowerPoint…"), never a developer-facing string.
 */

import {
  replaceSlideWithRewrittenCopy,
  unzipPptxBase64,
} from '../../deckorate/features/apply-brand/services/brandApplier';
import {
  NO_SLIDE_SELECTED_ERROR,
  NOTHING_TO_INSERT_ERROR,
  POWERPOINT_UNAVAILABLE_ERROR,
  toSlideText,
} from './insertIntoSlide';
import { appendToSlideNotes } from './notesAppend';

/** Thrown when this PowerPoint cannot export and re-insert slides (gap G-2). */
export const NOTES_UNAVAILABLE_ERROR = 'Speaker notes unavailable on this PowerPoint host';

/** Office.js error codes for an API this host does not implement. */
const MISSING_API_CODES: ReadonlySet<string> = new Set(['ApiNotFound', 'NotImplemented']);

export interface AddSpeakerNotesResult {
  readonly added: true;
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

function isMissingApi(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && MISSING_API_CODES.has(code);
}

/**
 * The slide exported as a base64 package. Hosts older than PowerPointApi 1.8
 * either lack the method or reject it at sync; both are reported as
 * {@link NOTES_UNAVAILABLE_ERROR}.
 */
async function exportSlide(context: PowerPoint.RequestContext, slide: PowerPoint.Slide): Promise<string> {
  const exportable = slide as unknown as { exportAsBase64?: () => OfficeExtension.ClientResult<string> };
  if (typeof exportable.exportAsBase64 !== 'function') throw new Error(NOTES_UNAVAILABLE_ERROR);
  try {
    const exported = exportable.exportAsBase64();
    await context.sync();
    return exported.value;
  } catch (e) {
    if (isMissingApi(e)) throw new Error(NOTES_UNAVAILABLE_ERROR);
    throw e;
  }
}

/**
 * Select the slide now at `index` — the rewritten copy, which took the
 * original's place. Best-effort: if the host cannot select, the notes are still
 * added, which is what the user asked for.
 */
async function selectSlideAt(context: PowerPoint.RequestContext, index: number): Promise<void> {
  try {
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();
    const copy = slides.items[index];
    if (!copy) return;
    context.presentation.setSelectedSlides([copy.id]);
    await context.sync();
  } catch {
    /* selection API unavailable on this host */
  }
}

/**
 * Append `text` to the selected slide's speaker notes.
 *
 * Throws {@link NO_SLIDE_SELECTED_ERROR}, {@link NOTES_UNAVAILABLE_ERROR},
 * {@link POWERPOINT_UNAVAILABLE_ERROR} or {@link NOTHING_TO_INSERT_ERROR}; any
 * other error is a host failure.
 */
export async function addSpeakerNotes(text: string): Promise<AddSpeakerNotesResult> {
  if (!hasPpt()) throw new Error(POWERPOINT_UNAVAILABLE_ERROR);
  const notes = toSlideText(text);
  if (!notes) throw new Error(NOTHING_TO_INSERT_ERROR);

  return await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load('items/id');
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();
    const slide = selected.items[0];
    if (!slide) throw new Error(NO_SLIDE_SELECTED_ERROR);
    if (typeof context.presentation.insertSlidesFromBase64 !== 'function')
      throw new Error(NOTES_UNAVAILABLE_ERROR);

    const files = await unzipPptxBase64(await exportSlide(context, slide));
    appendToSlideNotes(files, notes);
    await replaceSlideWithRewrittenCopy(
      context,
      files,
      slide.id,
      'KeepSourceFormatting' as PowerPoint.InsertSlideFormatting
    );
    await selectSlideAt(
      context,
      slides.items.findIndex((s) => s.id === slide.id)
    );
    return { added: true as const };
  });
}
